import React, { useMemo } from 'react';
import { ParticipationStats, ParticipationStatus } from '@app/api';
import uniq from 'lodash/uniq';
import {
  LabelList,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';

import { Theme } from '@app/theme';
import { useTranslation } from 'react-i18next';
import { createUseStyles, useTheme } from 'react-jss';
import { StatsCard } from './StatsCard';

const useStyles = createUseStyles((theme: Theme) => ({
  container: {
    ...theme.fonts.label,
  },
  label: {
    ...theme.fonts.caption,
  },
}));

interface Props {
  analytics: ParticipationStats[];
  studyType?: string;
}
const orderedStatuses = [
  ParticipationStatus.INVITED,
  ParticipationStatus.NOT_INTERESTED,
  ParticipationStatus.APPLIED,
  ParticipationStatus.ACCEPTED,
  ParticipationStatus.IN_PROGRESS,
  ParticipationStatus.BOOKED,
  ParticipationStatus.DECLINED,
  ParticipationStatus.CANCELED,
  ParticipationStatus.PARTICIPATED,
  ParticipationStatus.NO_SHOW,
  ParticipationStatus.PAYMENT_REQUESTED,
  ParticipationStatus.PROCESSING_PAYMENT,
  ParticipationStatus.PAYED,
];

export const ParticipationsCount = ({ analytics, studyType }: Props) => {
  const { t } = useTranslation();
  const theme = useTheme<Theme>();
  const classes = useStyles();

  const data = useMemo(() => {
    const byStatus: Record<string, Record<string, number>> = {};

    for (const track of analytics) {
      const status = track.status;
      const source = track.source || 'Empty';

      if (!byStatus[status]) {
        byStatus[status] = {};
      }
      if (!byStatus[status][source]) {
        byStatus[status][source] = 0;
      }
      byStatus[status][source] += track.count;

      if (!byStatus[status].total) {
        byStatus[status].total = 0;
      }
      byStatus[status].total += track.count;
    }
    return orderedStatuses.map((status) => {
      return Object.entries(byStatus[status] || {}).reduce(
        (acc, [source, count]) => {
          acc[source] = count;
          return acc;
        },
        {
          step: status,
          status: t(`sessions.details.monitoring.status.${status}`, {
            context: studyType?.toLowerCase(),
          }),
        } as any,
      );
    });
  }, [analytics, t]);

  const legend = useMemo(
    // source = "ref" cause a bug. So we add a space
    () => uniq(analytics.map((a) => a.source)),
    [analytics],
  );

  const colors = useMemo(
    () => [
      theme.colors.blue,
      theme.colors.red,
      theme.colors.green,
      theme.colors.accentAccessible,
      theme.colors.yellowAccessible,
    ],
    [theme],
  );

  return (
    <StatsCard title={t('sessions.details.stats.participations')}>
      <ResponsiveContainer height={300} className={classes.container}>
        <BarChart
          width={800}
          height={300}
          data={data}
          margin={{
            top: theme.marginBase * 3,
            right: theme.marginBase * 3,
            bottom: theme.marginBase * 3,
          }}
        >
          <XAxis dataKey="status" tick={theme.fonts.caption} />
          <YAxis
            tick={theme.fonts.caption}
            scale="sqrt"
            domain={[1, 'auto']}
            dataKey="total"
          />
          <Tooltip />
          {legend.length > 1 && <Legend />}
          {legend.map((source, index) => (
            <Bar
              dataKey={source}
              fill={colors[index % colors.length]}
              key={source}
              stackId="a"
            >
              {index === legend.length - 1 && (
                <LabelList
                  className={classes.label}
                  dataKey="total"
                  position="top"
                  fill={theme.colors.inkSecondary}
                />
              )}
            </Bar>
          ))}
        </BarChart>
      </ResponsiveContainer>
    </StatsCard>
  );
};
